import * as React from 'react';
import { Helmet } from 'react-helmet';

export function serializeJsonLd(data: any): string {
  return JSON.stringify(data).replace(/</g, '\\u003c');
}

interface JsonLdProps {
  data: any;
}

function JsonLd({ data }: JsonLdProps) {
  return (
    <Helmet>
      <script type="application/ld+json">{serializeJsonLd(data)}</script>
    </Helmet>
  );
}

interface ContactPointData {
  telephone?: string;
  email?: string;
  contactType?: string;
  availableLanguage?: string;
}

interface OrganizationMicrodataProps {
  name: string;
  url: string;
  logo: string;
  sameAs?: string[];
  contactPoint?: ContactPointData;
}

export function OrganizationMicrodata({
  name,
  url,
  logo,
  sameAs,
  contactPoint,
}: OrganizationMicrodataProps) {
  const data: any = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url,
    logo,
  };

  if (sameAs && sameAs.length > 0) {
    data.sameAs = sameAs;
  }

  if (contactPoint && (contactPoint.telephone || contactPoint.email)) {
    data.contactPoint = {
      '@type': 'ContactPoint',
      contactType: contactPoint.contactType || 'customer service',
      telephone: contactPoint.telephone,
      email: contactPoint.email,
      availableLanguage: contactPoint.availableLanguage,
    };
  }

  return <JsonLd data={data} />;
}

export interface BreadcrumbItemData {
  name: string;
  url: string;
}

interface BreadcrumbMicrodataProps {
  items: BreadcrumbItemData[];
}

export function BreadcrumbMicrodata({ items }: BreadcrumbMicrodataProps) {
  const validItems = (items || []).filter(item => item && item.name);

  if (validItems.length < 2) {
    return null;
  }

  const data = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: validItems.map((item, index) => {
      const listItem: any = {
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
      };

      if (item.url) {
        listItem.item = item.url;
      }

      return listItem;
    }),
  };

  return <JsonLd data={data} />;
}

interface WebSiteMicrodataProps {
  name: string;
  url: string;
  searchUrlTemplate?: string;
  inLanguage?: string;
}

export function WebSiteMicrodata({
  name,
  url,
  searchUrlTemplate,
  inLanguage,
}: WebSiteMicrodataProps) {
  if (!name || !url) {
    return null;
  }

  const data: any = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name,
    url,
  };

  if (inLanguage) {
    data.inLanguage = inLanguage;
  }

  if (searchUrlTemplate) {
    data.potentialAction = {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: searchUrlTemplate,
      },
      'query-input': 'required name=search_term_string',
    };
  }

  return <JsonLd data={data} />;
}

export interface ArticleAuthorData {
  name?: string;
  url?: string;
  linkedin?: string;
}

export interface ArticlePublisherData {
  name?: string;
  logo?: string;
  url?: string;
}

interface ArticleMicrodataProps {
  type?: 'BlogPosting' | 'WebPage';
  headline: string;
  url?: string;
  image?: string;
  description?: string;
  datePublished?: string;
  dateModified?: string;
  inLanguage?: string;
  author?: ArticleAuthorData;
  publisher?: ArticlePublisherData;
}

export function ArticleMicrodata({
  type,
  headline,
  url,
  image,
  description,
  datePublished,
  dateModified,
  inLanguage,
  author,
  publisher,
}: ArticleMicrodataProps) {
  if (!headline) {
    return null;
  }

  const schemaType = type || 'BlogPosting';

  const data: any = {
    '@context': 'https://schema.org',
    '@type': schemaType,
  };

  if (schemaType === 'WebPage') {
    data.name = headline;
  } else {
    data.headline = headline;
  }

  if (url) {
    data.url = url;
    data.mainEntityOfPage = {
      '@type': 'WebPage',
      '@id': url,
    };
  }

  if (image) {
    data.image = [image];
  }

  if (description) {
    data.description = description;
  }

  if (datePublished) {
    data.datePublished = datePublished;
  }

  if (dateModified) {
    data.dateModified = dateModified;
  }

  if (inLanguage) {
    data.inLanguage = inLanguage;
  }

  if (author && author.name) {
    const person: any = {
      '@type': 'Person',
      name: author.name,
    };

    if (author.url) {
      person.url = author.url;
    }

    if (author.linkedin) {
      person.sameAs = [author.linkedin];
    }

    data.author = person;
  }

  if (publisher && publisher.name) {
    const organization: any = {
      '@type': 'Organization',
      name: publisher.name,
    };

    if (publisher.url) {
      organization.url = publisher.url;
    }

    if (publisher.logo) {
      organization.logo = {
        '@type': 'ImageObject',
        url: publisher.logo,
      };
    }

    data.publisher = organization;
  }

  return <JsonLd data={data} />;
}

export interface ItemListItemData {
  name?: string;
  url?: string;
}

interface ItemListMicrodataProps {
  name?: string;
  url?: string;
  items: ItemListItemData[];
  startPosition?: number;
}

export function ItemListMicrodata({
  name,
  url,
  items,
  startPosition,
}: ItemListMicrodataProps) {
  const validItems = (items || []).filter(item => item && item.url);

  if (validItems.length === 0) {
    return null;
  }

  const offset = startPosition && startPosition > 0 ? startPosition : 0;

  const itemList: any = {
    '@type': 'ItemList',
    numberOfItems: validItems.length,
    itemListElement: validItems.map((item, index) => {
      const listItem: any = {
        '@type': 'ListItem',
        position: offset + index + 1,
        url: item.url,
      };

      if (item.name) {
        listItem.name = item.name;
      }

      return listItem;
    }),
  };

  const data: any = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    mainEntity: itemList,
  };

  if (name) {
    data.name = name;
  }

  if (url) {
    data.url = url;
  }

  return <JsonLd data={data} />;
}

export interface ProductOfferData {
  price?: string;
  priceCurrency?: string;
  availability?: string;
  url?: string;
}

interface ProductMicrodataProps {
  name: string;
  image?: string[];
  url?: string;
  brand?: string;
  description?: string;
  sku?: string;
  offer?: ProductOfferData;
}

export function ProductMicrodata({
  name,
  image,
  url,
  brand,
  description,
  sku,
  offer,
}: ProductMicrodataProps) {
  if (!name) {
    return null;
  }

  const data: any = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
  };

  if (image && image.length > 0) {
    data.image = image;
  }

  if (url) {
    data.url = url;
  }

  if (brand) {
    data.brand = {
      '@type': 'Brand',
      name: brand,
    };
  }

  if (description) {
    data.description = description;
  }

  if (sku) {
    data.sku = sku;
  }

  if (offer && offer.price && offer.priceCurrency) {
    data.offers = {
      '@type': 'Offer',
      price: offer.price,
      priceCurrency: offer.priceCurrency,
      availability: offer.availability,
      url: offer.url,
    };
  }

  return <JsonLd data={data} />;
}
